import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { DiarizationContext } from '@app/modules/logger/screens/stream/diarization/DiarizationContext';
import { useStreamSegmentsRange } from '@app/modules/logger/screens/stream/hooks/segments/useStreamSegmentsQuery';
import { useContext, useMemo } from 'react';
import { usePersonsByIds } from '../hooks/usePersonsByIds';
import { buildPersonLanes } from './build-person-lanes';
import { isFaceRecognitionEnabled } from './is-face-recognition-enabled';
import { mapDiarizationToAppearances } from './map-appearances';
import { PersonLane } from './types';
import { useFaceAppearances } from './useFaceAppearances';

/**
 * Lanes de personas para el rango de segmentos cargado: voz desde DiarizationContext
 * (ya calienta Person API para sus person_ids) + rostro desde PNT facerecognition.
 */
export function usePersonLanes(): { lanes: PersonLane[]; isLoading: boolean } {
  const station = useActiveStation();
  const range = useStreamSegmentsRange();
  const { diarization, speakers } = useContext(DiarizationContext);

  const { appearances: face, isLoading } = useFaceAppearances(
    station.slug,
    range,
    isFaceRecognitionEnabled(station),
  );

  const voice = useMemo(() => mapDiarizationToAppearances(diarization), [diarization]);

  const facePersonIds = useMemo(
    () => face.map((a) => a.personId).filter((id): id is string => !!id),
    [face],
  );
  // Calienta el cache individual de usePerson para las lanes de rostro.
  usePersonsByIds(facePersonIds);

  const lanes = useMemo(
    () => buildPersonLanes([...voice, ...face], speakers),
    [voice, face, speakers],
  );

  return { lanes, isLoading };
}
